import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RevealOnScroll, FadeUp } from "../../lib/motion";
import { useLang } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", subject: "", message: "" };

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(c.toastFill);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.toastSuccess);
      setForm(initial);
    } catch (err) {
      toast.error(c.toastError);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-black py-4 text-lg outline-none placeholder:text-neutral-400 focus:border-black transition-colors";

  return (
    <section id="contact" className="bg-white" data-testid="contact-section">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <div>
            <FadeUp>
              <span className="text-sm md:text-base uppercase tracking-[0.25em] font-bold text-neutral-500">
                {c.label}
              </span>
            </FadeUp>
            <h2 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.88] tracking-tighter mt-6">
              {c.titleLines.map((line, i) => (
                <RevealOnScroll key={i} delay={i * 0.1}>
                  {line}
                </RevealOnScroll>
              ))}
            </h2>

            <div className="mt-12 space-y-8">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
                  {c.emailLabel}
                </div>
                <a
                  href="mailto:hmbestsportsconsulting@gmail.com"
                  data-testid="contact-email"
                  className="text-lg md:text-xl font-medium border-b border-black hover:opacity-60 transition-opacity break-all"
                >
                  hmbestsportsconsulting@gmail.com
                </a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">
                  {c.instagramLabel}
                </div>
                <a
                  href="https://www.instagram.com/hmsportconsulting"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="text-lg md:text-xl font-medium border-b border-black hover:opacity-60 transition-opacity"
                >
                  @hmsportconsulting
                </a>
              </div>
            </div>
          </div>

          <FadeUp delay={0.1}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-8">
              <div className="grid md:grid-cols-2 gap-8">
                <input
                  className={field}
                  name="name"
                  placeholder={c.name}
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-name"
                />
                <input
                  className={field}
                  type="email"
                  name="email"
                  placeholder={c.email}
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-email-input"
                />
              </div>
              <input
                className={field}
                name="subject"
                placeholder={c.subject}
                value={form.subject}
                onChange={onChange}
                data-testid="contact-subject"
              />
              <textarea
                className={`${field} resize-none`}
                name="message"
                rows={4}
                placeholder={c.message}
                value={form.message}
                onChange={onChange}
                data-testid="contact-message"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="self-start px-10 py-5 bg-black text-white text-xs uppercase tracking-[0.25em] font-bold border border-black transition-colors duration-300 hover:bg-white hover:text-black disabled:opacity-50"
              >
                {loading ? c.sending : c.submit}
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

export default Contact;
